import { use, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { AccessDeniedFallback } from '@/components/error-boundaries/fallbacks/access-denied-fallback'
import { GenericErrorFallback } from '@/components/error-boundaries/fallbacks/generic-error-fallback'
import { SamlAuthRequiredFallback } from '@/components/error-boundaries/fallbacks/saml-auth-required-fallback'
import { SamlSessionGuard } from '@/components/error-boundaries/saml-session-guard'
import {
  FraudSuspensionError,
  NoAccessError,
  SamlAuthRequiredError,
} from '@/lib/errors'
import { useIdCompany, useRBAC } from '@/lib/hooks'

const ACCESS_DENIED_MESSAGE =
  'Something went wrong or you don\u2019t have the needed permission. Double-check and try again.'
const DYNAMIC_IMPORT_MESSAGE = 'Failed to fetch dynamically imported module'

export const PageErrorBoundary = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const { isSuspended, suspensionReason } = use(CompanySuspensionContext)

  useEffect(() => {
    const isSuperadmin = rbac.kinsta().superadmin.can()
    if (isSuspended && suspensionReason === 'fraud' && !isSuperadmin) {
      throw new FraudSuspensionError()
    }
  }, [rbac, isSuspended, suspensionReason])

  return (
    <ErrorBoundary
      key={idCompany}
      onReset={() => {
        window.location.href = window.location.origin
      }}
      onError={(error) => {
        if (error?.message?.includes?.(DYNAMIC_IMPORT_MESSAGE)) {
          window.location.reload()
        }
      }}
      fallbackRender={({ error, resetErrorBoundary }) => {
        const isSamlAuthRequired =
          error.name === 'SamlAuthRequiredError' ||
          error instanceof SamlAuthRequiredError

        if (isSamlAuthRequired) {
          return <SamlAuthRequiredFallback error={error} />
        }

        const isAccessDenied =
          error.message === ACCESS_DENIED_MESSAGE ||
          error instanceof NoAccessError

        if (error?.message?.includes?.(DYNAMIC_IMPORT_MESSAGE)) {
          return null
        }

        return isAccessDenied ? (
          <AccessDeniedFallback />
        ) : (
          <GenericErrorFallback
            error={error}
            resetErrorBoundary={resetErrorBoundary}
          />
        )
      }}
    >
      <SamlSessionGuard />
      {children}
    </ErrorBoundary>
  )
}
